if (!customElements.get('details-disclosure-controller')) {
  class DetailsDisclosureController extends HTMLElement {
    constructor () {
      super();

      const FIRST_OPENED_VALUES = ['both', 'mobile', 'desktop'];
      this.mobileMql = window.matchMedia('(max-width: 749px)');
      const raw = this.getAttribute('data-first-opened');
      const closeOthersRaw = this.getAttribute('data-close-others');
      this.firstOpened = FIRST_OPENED_VALUES.includes(raw) ? raw : null;
      this.closeOthersOnToggle = closeOthersRaw === null ? true : closeOthersRaw !== 'false';
      this.detailsElements = Array.from(this.querySelectorAll('details'));
      this.userInteractedDetails = null;
      this._detailsListenersAttached = false;
      this._detailsListenersCleanup = [];

      this._onModeChange = () => this.applyFirstOpenByMode();
    }

    attachDetailsListeners () {
      if (this._detailsListenersAttached) return;
      this._detailsListenersCleanup = [];

      this.detailsElements.forEach((details) => {
        const summary = details.querySelector('summary');
        if (summary) {
          const onSummaryClick = () => {
            this.userInteractedDetails = details;
          };
          const onSummaryKeydown = (event) => {
            const isEnter = event.key === 'Enter';
            const isSpace = event.key === ' ' || event.code === 'Space';
            if (isEnter || isSpace) {
              this.userInteractedDetails = details;
            }
          };

          summary.addEventListener('click', onSummaryClick);
          summary.addEventListener('keydown', onSummaryKeydown);
          this._detailsListenersCleanup.push(() => {
            summary.removeEventListener('click', onSummaryClick);
            summary.removeEventListener('keydown', onSummaryKeydown);
          });
        }

        const onToggle = () => {
          const openedManually = this.userInteractedDetails === details;
          if (details.open && this.closeOthersOnToggle) {
            this.closeOthers(details, { manageFocusAndScroll: openedManually });
          }

          if (openedManually) {
            this.userInteractedDetails = null;
          }
        };

        details.addEventListener('toggle', onToggle);
        this._detailsListenersCleanup.push(() => {
          details.removeEventListener('toggle', onToggle);
        });
      });
      this._detailsListenersAttached = true;
    }

    detachDetailsListeners () {
      if (!this._detailsListenersAttached) return;
      this._detailsListenersCleanup.forEach((cleanup) => cleanup());
      this._detailsListenersCleanup = [];
      this._detailsListenersAttached = false;

    }

    connectedCallback () {
      this.attachDetailsListeners();
      this._listening = false;
      if (this.firstOpened === 'mobile' || this.firstOpened === 'desktop') {
        this._listening = true;
        if (typeof this.mobileMql.addEventListener === 'function') {
          this.mobileMql.addEventListener('change', this._onModeChange);
        } else {
          this.mobileMql.addListener(this._onModeChange);
        }
      }

      this.applyFirstOpenByMode();
    }

    disconnectedCallback () {
      this.detachDetailsListeners();
      if (!this._listening) return;

      if (typeof this.mobileMql.removeEventListener === 'function') {
        this.mobileMql.removeEventListener('change', this._onModeChange);
      } else {
        this.mobileMql.removeListener(this._onModeChange);
      }
    }

    shouldOpenFirstNow () {
      if (!this.firstOpened) return false;
      const isMobile = this.mobileMql.matches;

      if (this.firstOpened === 'both') return true;
      if (this.firstOpened === 'mobile') return isMobile;
      if (this.firstOpened === 'desktop') return !isMobile;

      return false;
    }

    applyFirstOpenByMode () {
      if (!this.firstOpened || this.detailsElements.length === 0) return;

      const first = this.detailsElements[0];
      const mustBeOpen = this.shouldOpenFirstNow();

      if (mustBeOpen) {
        this.openDetails(first);
        if (this.closeOthersOnToggle) {
          this.closeOthers(first, { manageFocusAndScroll: false });
        }
      } else {
        this.closeDetails(first);
      }
    }

    openDetails (details) {
      if (!details.open) details.setAttribute('open', '');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');
    }

    closeDetails (details) {
      if (details.open) details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }

    closeOthers (openedDetails, { manageFocusAndScroll = true } = {}) {
      this.detailsElements.forEach((details) => {
        if (details !== openedDetails && details.open) {
          details.removeAttribute('open');
          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'false');
        }
      });

      if (!manageFocusAndScroll) return;

      openedDetails.setAttribute('tabindex', '-1');
      openedDetails.focus({ preventScroll: false });

      requestAnimationFrame(() => {
        const header = document.querySelector('.sections-header-wrapper');
        const headerHeight = header?.offsetHeight || 0;

        const rect = openedDetails.getBoundingClientRect();
        if (rect.top < headerHeight) {
          const scrollTo = window.scrollY + rect.top - headerHeight;
          window.scrollTo({ top: scrollTo - 24, behavior: 'smooth' });
        }
      });
    }

  }

  customElements.define('details-disclosure-controller', DetailsDisclosureController);
}
